import React from 'react';
import { Chip, Tooltip, alpha, useTheme } from '@mui/material';
import { lifecycleTone } from './itemStatusTones';

/**
 * ITEM-ACTIVE.4 — the retire act, at the end of the row it belongs to.
 *
 * This replaced the checkbox-and-toolbar dance. Ticking a box and then hunting
 * upward for buttons that appeared somewhere above is two context switches for
 * one small decision.
 *
 * QUIET AT REST. The control is invisible until the row is hovered or
 * something in it takes keyboard focus — the list stays a list. `focus-within`
 * is what keeps that honest: hover-only would put the act out of reach of a
 * keyboard and out of reach on touch, so opacity is driven by BOTH and the
 * chip stays in the tab order throughout.
 *
 * ITEM-ACTIVE.4b — A STOCKED ROW NO LONGER SAYS "Has stock".
 *
 * That label assumed the reader had already met the feature. Someone seeing
 * the list for the first time got a two-word status in an action column,
 * explaining nothing and offering nothing they could act on. It also broke the
 * column's own rule: every other row shows nothing at rest, and this one
 * shouted a state that the Status column two cells to the left was already
 * reporting.
 *
 * So a stocked row now looks exactly like a healthy one at rest — empty — and
 * reveals a MUTED "Retire" on approach, whose tooltip explains itself in a
 * sentence: "Can't retire — 2 in stock. Click to see options." The verb is the
 * same word as everywhere else, the muting says it will not go through, and
 * the sentence says why and what to do. Clicking still opens the doors dialog,
 * so it is a way in rather than a dead end.
 */

export interface ItemRowRetireActionProps {
  active: boolean;
  /** Non-zero stock: the flip is refused and this opens the doors dialog. */
  blocked: boolean;
  /** How much is in the way, so the tooltip can say it. */
  quantity?: number;
  canEdit: boolean;
  busy?: boolean;
  onRetire: () => void;
  onReactivate: () => void;
  onResolveStock: () => void;
  itemName: string;
}

const ItemRowRetireAction: React.FC<ItemRowRetireActionProps> = ({
  active, blocked, quantity = 0, canEdit, busy = false,
  onRetire, onReactivate, onResolveStock, itemName,
}) => {
  const theme = useTheme();
  if (!canEdit) return null;

  const retiredTone = lifecycleTone(false);

  // Three shapes, one slot — and the blocked one is the same VERB as the
  // others, muted, rather than a different word that reads as a status.
  const spec = !active
    ? {
      label: 'Reactivate',
      color: theme.palette.text.secondary,
      bg: alpha(theme.palette.text.primary, 0.06),
      hoverBg: alpha(theme.palette.text.primary, 0.12),
      onClick: onReactivate,
      aria: `Reactivate ${itemName}`,
      tip: '',
    }
    : blocked
      ? {
        label: 'Retire',
        color: theme.palette.text.disabled,
        bg: alpha(theme.palette.text.primary, 0.04),
        hoverBg: alpha(theme.palette.text.primary, 0.08),
        onClick: onResolveStock,
        aria: `Retire ${itemName} — cannot retire, ${quantity} in stock`,
        // A sentence, not a label. Says what is wrong, by how much, and what
        // pressing it will do.
        tip: `Can’t retire — ${quantity} in stock. Click to see options.`,
      }
      : {
        label: 'Retire',
        color: retiredTone.color,
        bg: retiredTone.bg,
        hoverBg: retiredTone.bg,
        onClick: onRetire,
        aria: `Retire ${itemName}`,
        tip: '',
      };

  const chip = (
    <Chip
      label={spec.label}
      size="small"
      clickable={!busy}
      onClick={busy ? undefined : spec.onClick}
      aria-label={spec.aria}
      className="row-retire-action"
      sx={{
        fontSize: '0.68rem',
        height: 20,
        fontWeight: 600,
        color: spec.color,
        backgroundColor: spec.bg,
        border: 'none',
        cursor: busy ? 'default' : 'pointer',
        // Quiet at rest. The row reveals it; focus reveals it too, so a
        // keyboard user never chases an invisible target.
        opacity: 0,
        transition: 'opacity 120ms ease',
        'tr:hover &, tr:focus-within &': { opacity: 1 },
        '&:focus-visible': {
          opacity: 1,
          outline: '2px solid',
          outlineColor: spec.color,
        },
        '&:hover': { backgroundColor: spec.hoverBg, filter: 'brightness(0.97)' },
        // A coarse pointer has no hover, so nothing would ever reveal it.
        '@media (hover: none)': { opacity: 1 },
      }}
    />
  );

  // Only the blocked state carries a tooltip — the other two say everything
  // they need to in one word.
  return spec.tip ? <Tooltip title={spec.tip} arrow>{chip}</Tooltip> : chip;
};

export default ItemRowRetireAction;
